/**
 * SOLID Review
 * S — Strong. One concern: a roster tile's measured width — null until measured, full size then.
 * O — Good. Any element; nothing here names a tile's buttons.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The width.
 * D — Strong. The platform's resize observer; the element its view hands it.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IServiceResizeObserver } from './platform/IConfigDepViewPlatform';

export interface IStateTileWidth {
  /** null: unmeasured — the tile draws at full size. */
  readonly tileWidth: number | null;
}

const UNMEASURED: IStateTileWidth = { tileWidth: null };

/**
 * A ROSTER TILE'S WIDTH (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/topSection/RosterTile.tsx`'s state and
 * effect: the tile's own width as the browser reports it, so its buttons shrink when it is narrow. Where the browser observes no sizes
 * (old Safari) it stays unmeasured, at full size.
 */
export class TileWidthModel implements IServiceViewModel<IStateTileWidth> {
  private readonly state = new ObservableState<IStateTileWidth>(UNMEASURED);
  private readonly reactions = new Reactions<null>([{
    deps: () => [],
    run: () => {
      /* c8 ignore next -- jsdom has no ResizeObserver; the measured path is exercised in the compact-mode test */
      if (!this.observer.available() || !this.tile.current) return undefined;
      return this.observer.observe(this.tile.current, (entries) => {
        const measured: Partial<IStateTileWidth> = { tileWidth: entries[0]!.contentRect.width };
        this.state.update(measured);
      });
    },
  }]);

  constructor(
    private readonly observer: IServiceResizeObserver,
    private readonly tile: { readonly current: Element | null },
  ) {}

  getSnapshot(): IStateTileWidth {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(): void {
    this.reactions.stopChanged(null);
  }

  commit(): void {
    this.reactions.startChanged(null);
  }
}
